/**
 * The resolution funnel — one road from "something is being attempted" to
 * "here is what the dice said."
 *
 * `GameSystemAdapter` has carried `rollCheck` and `interpretResult` since it was
 * written and nothing ever called them. Every check, save, attack and initiative
 * roll in the app built its own `1d20` string instead, which is why changing a
 * campaign's `systemId` today changes the GM's tone and the character sheet and
 * not one die. This module is the road those call sites move onto.
 *
 * WHAT BELONGS HERE: resolution — a roll made against a rule, where the system
 * decides what the number means. Checks, saves, attacks, initiative.
 *
 * WHAT DOES NOT: free dice. `rollDice('2d6+3')` for a damage expression or the
 * dice tray is not a resolution and has no system opinion to consult. Sending
 * it through here would only add ceremony.
 *
 * ON FAILING LOUDLY: a roll whose system cannot be found throws. The tempting
 * alternative — fall back to 5e — is the exact bug this work exists to remove,
 * because a table playing under the wrong rules has no way to notice. A crash
 * is recoverable; a session silently resolved against another game's maths is
 * not.
 */
import { readingForRoll } from './system-runtime/resolution-variants';
import { getActiveSystem, getSystem, } from './system-adapter';
// =============================================================================
// ERRORS
// =============================================================================
/**
 * A roll named a system that nothing registered.
 *
 * Almost always a campaign whose `settings.systemId` points at a package that
 * was never imported in `apps/{api,web}/src/systems.ts`.
 */
export class UnknownSystemError extends Error {
    systemId;
    constructor(systemId) {
        super(`No game system registered for "${systemId}". ` +
            `Register its adapter in apps/{api,web}/src/systems.ts before resolving rolls against it.`);
        this.systemId = systemId;
        this.name = 'UnknownSystemError';
    }
}
/** A roll was attempted with no system named and none active. */
export class NoActiveSystemError extends Error {
    constructor() {
        super('Cannot resolve a roll: no system named on the context and no active system. ' +
            'Call activateCampaignSystem() on campaign entry, or pass systemId on the RollContext.');
        this.name = 'NoActiveSystemError';
    }
}
// =============================================================================
// RESOLVING WHICH SYSTEM
// =============================================================================
/**
 * The adapter a roll resolves against, in order of how specific the answer is.
 *
 * An explicit adapter beats the context, and the context beats global state.
 * The server needs the first two because it holds many campaigns at once; the
 * client normally needs neither, because the active system IS the campaign the
 * player is in.
 */
export function adapterForRoll(context, override) {
    if (override)
        return override;
    if (context.systemId) {
        const named = getSystem(context.systemId);
        if (!named)
            throw new UnknownSystemError(context.systemId);
        return named;
    }
    const active = getActiveSystem();
    if (!active)
        throw new NoActiveSystemError();
    return active;
}
/** The critical outcomes, or null — see {@link ResolvedRoll.criticality}. */
function criticalityOf(adapter, roll, context) {
    const outcome = adapter.interpretResult(roll, context).outcome;
    return outcome === 'critical-success' || outcome === 'critical-failure' ? outcome : null;
}
/**
 * Roll something against the rules of the system it belongs to.
 *
 * This is the only function in the app that should be turning an attempt into
 * dice. If you find yourself writing `'1d20'` next to a modifier, you want this
 * instead.
 */
export function resolveRoll(context, override) {
    const adapter = adapterForRoll(context, override);
    const roll = adapter.rollCheck(context);
    return {
        systemId: adapter.id,
        roll,
        interpretation: interpretationOf(adapter, roll, context),
        criticality: criticalityOf(adapter, roll, context),
    };
}
/**
 * The system's reading of a roll, or null when it declined to give one.
 *
 * THIS USED TO ASK THE WRONG QUESTION. It withheld interpretation whenever
 * `context.dc` was undefined, on the reasoning that a check with no difficulty
 * is a number waiting for a GM and that inventing a DC produces confident
 * nonsense. Both halves of that are still true — for d20. They are false for a
 * system that has no target numbers at all: Trophy Dark reads 6 / 4-5 / 1-3
 * straight off the die and never had a DC to be missing, so a funnel keyed to
 * the absence of one returned "no outcome" for every roll in the game.
 *
 * That was a d20 assumption living in the one place that was supposed to have
 * none, and it survived Phase 0's whole audit because every system in the
 * repository at the time did use target numbers. It took a system that does not
 * to find it.
 *
 * So the question moves to where it belongs: the adapter always gets asked, and
 * a system that cannot judge the roll says so by returning `undecided`.
 */
function interpretationOf(adapter, roll, context) {
    const interpretation = adapter.interpretResult(roll, context);
    return interpretation.outcome === 'undecided' ? null : interpretation;
}
/**
 * Did the roll land?
 *
 * Systems disagree about which degrees count as getting what you wanted —
 * a partial in a PbtA game is a hit with a cost, not a miss — so the question
 * is answered here once against `ResultInterpretation` rather than by every
 * caller comparing totals to target numbers.
 *
 * `partial` is deliberately NOT a success. A system that wants "yes, but" to
 * advance the fiction should read `outcome` directly; a caller that only asks
 * this yes/no question is asking whether the attempt cleanly worked.
 */
export function succeeded(interpretation) {
    if (!interpretation)
        return false;
    return interpretation.outcome === 'success' || interpretation.outcome === 'critical-success';
}
/** The system's best result — a nat 20 in d20 terms, whatever the system calls it. */
export function isCriticalSuccess(interpretation) {
    return interpretation?.outcome === 'critical-success';
}
/** The system's worst result. */
export function isCriticalFailure(interpretation) {
    return interpretation?.outcome === 'critical-failure';
}
// =============================================================================
// ABILITY MODIFIERS
// =============================================================================
/**
 * An ability score's modifier under the given system.
 *
 * `Math.floor((score - 10) / 2)` is written out by hand in several places in the
 * app. It is correct for every d20 system and wrong for everything else, and
 * each copy is a place a new system silently plays as D&D.
 */
export function abilityModifier(score, override) {
    const adapter = override ?? getActiveSystem();
    if (!adapter)
        throw new NoActiveSystemError();
    return adapter.getAbilityModifier(score);
}
// =============================================================================
// ANIMATED ROLLS — when the dice are somewhere else
// =============================================================================
/**
 * The dice a system rolls, as an expression a physical roller can animate.
 *
 * `resolveRoll` is for rolls the engine makes. The 3D dice tray is the other
 * case: the player watches real dice land, so the system has to be asked what
 * to throw BEFORE the throw and what it meant AFTER, with the animation in
 * between.
 *
 * GAP THIS PAPERS OVER: `CoreResolution` names a resolution *type* and no dice.
 * There is no field on the interface that says "1d20", so this maps the type
 * enum to the obvious expression and returns null for the families where that
 * question has no single answer. The declarative rules format should carry an
 * explicit dice expression — see EPIC-0045 Phase 2 — and this mapping should
 * shrink to a fallback when it does.
 *
 * `rollType` picks a resolution variant when the system declares one for it
 * (ISS-0156), so the tray throws the dice that kind of roll actually uses.
 * Without it the answer is the system's default dice.
 */
export function checkNotation(override, rollType) {
    const adapter = override ?? getActiveSystem();
    if (!adapter)
        throw new NoActiveSystemError();
    return notationFor(adapter, rollType);
}
/** The dice for one kind of roll, with any variant for that roll applied. */
function notationFor(adapter, rollType) {
    // A hand-built adapter can arrive with no resolution block at all, and the
    // answer then is "no dice I can name", same as before variants existed.
    const core = adapter.rules.coreResolution;
    return notationForResolution(core && readingForRoll(core, rollType).resolution);
}
function notationForResolution(resolution) {
    // A system that states its dice outright is believed over the enum, which is
    // the point of the field — a `custom` system has no other way to be rolled.
    if (resolution?.diceExpression)
        return resolution.diceExpression;
    switch (resolution?.type) {
        case 'd20-roll-high':
        case 'd20-roll-under':
            return '1d20';
        case '2d6-pbta':
            return '2d6';
        case 'fate-dice':
            return '4dF';
        case 'percentile':
        case 'd100':
            return '1d100';
        // A dice pool's size depends on the character, step dice on the trait, and
        // `custom` is custom. None of them can be answered without more than the
        // system's identity, so say so instead of guessing a d20.
        default:
            return null;
    }
}
/**
 * Interpret dice that were rolled somewhere else.
 *
 * For the animated path: the tray produced the number, and the system still
 * owns what it means. A call site comparing the tray's total to a DC itself is
 * the same bug as a call site writing `'1d20'` — it has quietly decided that
 * higher is better.
 *
 * Single-die shaped, because that is what the tray hands back today. Systems
 * that sum a pool need an adapter method that takes raw dice; when one exists,
 * this becomes a caller of it.
 */
export function interpretRolled(context, natural, override) {
    const adapter = adapterForRoll(context, override);
    const modifier = context.modifier ?? 0;
    const roll = {
        total: natural + modifier,
        dice: [natural],
        modifier,
        formula: `${notationFor(adapter, context.type) ?? 'dice'}${modifier >= 0 ? `+${modifier}` : modifier}`,
        natural,
    };
    return {
        systemId: adapter.id,
        roll,
        interpretation: context.dc === undefined ? null : adapter.interpretResult(roll, context),
        criticality: criticalityOf(adapter, roll, context),
    };
}
//# sourceMappingURL=resolution.js.map